import { useMemo } from "react";

import { useCan } from "components";
import { useRealTimeTypedQuery } from "components/WebSocket/hooks";
import { typedQueryClient } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { CarDetailsQueryKeys } from "modules/CarDetails/queryKeys";
import { ApiInstance } from "utils/Api";
import { groupAndSortItemsWithHistory } from "utils/itemsWithHistory";

import type { WebSocketMessageListener } from "components";
import type { Pin } from "models";

export const usePinItemsQuery = (car_id?: number) => {
  const canListPinItems = useCan(ACL_ACTIVITIES.List, ACL_RESOURCES.Pin);

  const listeners = useMemo(() => {
    if (!car_id) return [];

    const pinListeners: WebSocketMessageListener<Pin>[] = [
      {
        model: "Pin",
        action: "upsert",
        filter: { car_id },
        callback: message => {
          const pinItems = typedQueryClient.getQueryData(CarDetailsQueryKeys.pinItems);
          if (!pinItems) return;

          const pinData = message.data;
          typedQueryClient.setQueryData(CarDetailsQueryKeys.pinItems, pinItems.length ? [...pinItems, pinData] : [pinData]);
        }
      },
      {
        model: "Pin",
        action: "delete",
        filter: { car_id },
        callback: message => {
          const pinItems = typedQueryClient.getQueryData(CarDetailsQueryKeys.pinItems);
          if (!pinItems?.length) return;

          const pinData = message.data;
          const pinIdx = pinItems.findIndex(pin => pin.id === pinData.id);
          if (!pinIdx) return;

          typedQueryClient.setQueryData(CarDetailsQueryKeys.pinItems, pinItems.toSpliced(pinIdx, 1));
        }
      }
    ];

    return [...pinListeners];
  }, [car_id]);

  return useRealTimeTypedQuery({
    enabled: !!car_id && canListPinItems,
    queryKey: CarDetailsQueryKeys.pinItems,
    queryFn: async ({ baseUrl, endpoint }) => (await ApiInstance.post(endpoint, { car_id }, baseUrl)).data.items,
    listeners,
    select: groupAndSortItemsWithHistory
  });
};
